import {
  CircularProgress,
  Stack,
  Divider,
  Grid,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { CancelButtonStyled } from '@v2/components/campaign/CampaignAdGroupSection/styles';
import { Button } from '@v2/components/ui/Button';
import { Info } from '@v2/components/campaign/Info';
import {
  Controller,
  useForm,
  type FieldValues,
  type Resolver,
} from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, memo, useMemo } from 'react';
import { getSchema } from '../../validations';
import {
  EditAdGroupDatesTimesFormValues,
  EditAdGroupDatesTimesProps,
} from '../../constants/types';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ModalSheetDialog as Dialog } from '../styledComponents';
import { getDateTimeRange, getDefaultValues, getSameValues } from './utils';
import { useBulkEdit } from '../../context';
import SelectDateTime from '@v2/components/ui/SelectDateTime/SelectDateTime';

function createValidationResolver<T extends FieldValues>(
  schema: yup.AnyObjectSchema
): Resolver<T> {
  return yupResolver(schema) as Resolver<T>;
}

export const EditAdGroupDatesTimes = memo(function EditAdGroupDatesTimes({
  alreadyStarted = false,
  fieldValues,
  onSubmit,
  onCancel,
  onBack,
  isValidating = false,
  open,
  campaign,
  adGroups,
}: EditAdGroupDatesTimesProps) {
  const schema = getSchema(alreadyStarted, campaign);

  const { sameStart } = getSameValues(adGroups);
  const { minDateTime, maxDateTime } = getDateTimeRange(campaign);

  const methods = useForm<EditAdGroupDatesTimesFormValues>({
    defaultValues: getDefaultValues(adGroups, fieldValues || 'None'),
    mode: 'onChange',
    resetOptions: {
      keepDirtyValues: true,
    },
    resolver:
      createValidationResolver<EditAdGroupDatesTimesFormValues>(schema),
  });

  const {
    handleSubmit,
    formState: { isValid, dirtyFields },
    trigger,
    watch,
  } = methods;

  const dateTimeFields: Array<keyof EditAdGroupDatesTimesFormValues> =
    useMemo(() => ['startDateTime', 'endDateTime'], []);

  const [startDateTime, endDateTime] = watch(dateTimeFields);

  const { getHeaderText } = useBulkEdit();

  useEffect(() => {
    if (dateTimeFields.some(field => dirtyFields[field])) {
      trigger(dateTimeFields);
    }
  }, [startDateTime, endDateTime, trigger, dirtyFields, dateTimeFields]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="lg"
      sx={{ pt: 2, pb: 2 }}
      data-testid="edit-dates-times-dialog"
    >
      <form id="bulk-edit-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pt={1}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={onBack} size="large" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h3">{getHeaderText()}</Typography>
            </Box>

            <IconButton onClick={onCancel} size="large" aria-label="close">
              <CloseIcon data-testid="CloseIcon" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          <Stack sx={{ height: '100%', position: 'relative' }}>
            {/* Keep HEAD&apos;s validation overlay */}
            {isValidating && (
              <Stack
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={40} />
              </Stack>
            )}

            <Stack spacing={3} sx={{ alignItems: 'center', px: 3, py: 2 }}>
              {alreadyStarted && (
                <Info direction="column" type="info">
                  Start Date and Start Time are locked because one or more of
                  the selected ad groups are active.
                </Info>
              )}

              <Stack
                direction="row"
                spacing={3}
                sx={{ justifyContent: 'center', width: '60%' }}
              >
                <Controller
                  control={methods.control}
                  name="startDateTime"
                  render={({ field, fieldState }) => (
                    <SelectDateTime
                      {...field}
                      clearable
                      fieldState={fieldState}
                      value={field.value ?? null}
                      label={
                        alreadyStarted && !sameStart
                          ? 'Multiple'
                          : 'Start Date & Time (ET)'
                      }
                      minDateTime={minDateTime}
                      maxDateTime={maxDateTime}
                      disabled={alreadyStarted || isValidating}
                      lockIcon={alreadyStarted}
                      sx={{ flex: 1 }}
                      testId="start-date-time"
                    />
                  )}
                />

                <Controller
                  control={methods.control}
                  name="endDateTime"
                  render={({ field, fieldState }) => (
                    <SelectDateTime
                      {...field}
                      clearable
                      fieldState={fieldState}
                      value={field.value ?? null}
                      label="End Date & Time (ET)"
                      minDateTime={minDateTime}
                      maxDateTime={maxDateTime}
                      sx={{ flex: 1 }}
                      testId="end-date-time"
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions disableSpacing sx={{ px: 4, py: 3 }}>
          <Grid container direction="column" sx={{ width: '100%' }}>
            <Divider sx={{ width: '100%', mb: 3 }} />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ minHeight: 'auto' }}
            >
              <Grid>
                <CancelButtonStyled variant="text" onClick={onCancel}>
                  Cancel
                </CancelButtonStyled>
              </Grid>
              <Grid>
                <Button
                  variant="contained"
                  type="submit"
                  label="Next: Review Edits"
                  disabled={!isValid || isValidating}
                  sx={{ visibility: isValidating ? 'hidden' : 'visible' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
});
